import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import env from "shared/config/env";
import { randomA } from "shared/palettes";
import BottleFooter from "./components/BottleFooter";
import BottleHeader from "./components/BottleHeader";
import BottleJoin from "./components/BottleJoin";
import BottleResult from "./components/BottleResult";
import SpinMode from "./components/SpinMode";
import SpinPlayer from "./components/SpinPlayer";
import useBottleSocket from "./hooks/useBottleSocket";
import { ContainerStyle } from "./styled";

export default (props) => {
  const { duration } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [roomCode, setRoomCode] = useState(env.defaultRoomCode);
  const [userName, setUserName] = useState(
    () => randomA[Math.floor(Math.random() * randomA.length)]
  );
  const [drinkType, setDrinkType] = useState("เหล้า");
  const [userQuestion, setUserQuestion] = useState("");
  const [newQuestion, setNewQuestion] = useState("");

  const {
    isJoined,
    step,
    selectedMode,
    players,
    allQuestions,
    dareConfig,
    luckyUser,
    displayResult,
    lastModeResult,
    isDone,
    spunModes,
    rotationPlayer,
    rotationQuestion,
    rotationDare,
    socketRef,
    joinRoom,
    spinPlayer,
    selectMode,
    spinQuestion,
    spinDare,
    confirmTask,
    skipDare,
    addQuestion,
    resetRoom,
  } = useBottleSocket(roomCode, userName, userQuestion, drinkType);

  const isMyTurn = luckyUser?.id === socketRef.current?.id;
  const currentDareOptions =
    dareConfig[luckyUser?.drinkType] || dareConfig["เหล้า"] || [];
  const myPlayer = players.find((p) => p.id === socketRef.current?.id);
  // Default to 0 to prevent crashes if privileges aren't synced yet
  const myPrivileges = myPlayer?.privileges || { addQ: 0, skipD: 0 };

  const handleJoinRoom = () => {
    joinRoom();
  };

  if (!isJoined) {
    return (
      <motion.div
        initial={{ x: -10, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 10, opacity: 0 }}
        transition={{ duration: duration }}
      >
        <BottleJoin
          roomCode={roomCode}
          setRoomCode={setRoomCode}
          userName={userName}
          setUserName={setUserName}
          drinkType={drinkType}
          setDrinkType={setDrinkType}
          userQuestion={userQuestion}
          setUserQuestion={setUserQuestion}
          handleJoinRoom={handleJoinRoom}
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ x: -10, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 10, opacity: 0 }}
      transition={{ duration: duration }}
    >
      <ContainerStyle sx={{ pt: 2, pb: 5, minHeight: "100vh" }}>
        {/* --- Header --- */}
        <BottleHeader
          roomCode={roomCode}
          userName={userName}
          drinkType={drinkType}
          myPrivileges={myPrivileges}
        />

        <Grid container spacing={2} justifyContent="center">
          {/* Step 1: Spin Player */}
          <Grid item xs={12} sm={6}>
            <SpinPlayer
              step={step}
              isMobile={isMobile}
              players={players}
              userName={userName}
              luckyUser={luckyUser}
              rotationPlayer={rotationPlayer}
              handleSpinPlayer={() => {
                if (!(step >= 2 && !isDone)) {
                  spinPlayer();
                }
              }}
              isDone={isDone}
            />
          </Grid>

          {/* Step 2 & 3: Mode & Result */}
          <Grid item xs={12} sm={6}>
            <SpinMode
              step={step}
              isMyTurn={isMyTurn}
              luckyUser={luckyUser}
              selectedMode={selectedMode}
              isDone={isDone}
              handleSelectMode={selectMode}
              isMobile={isMobile}
              spinQuestion={() => {
                if (!(!isMyTurn || step === 3 || spunModes.truth || isDone)) {
                  spinQuestion();
                }
              }}
              rotationQuestion={rotationQuestion}
              spunModes={spunModes}
              allQuestions={allQuestions}
              spinDare={() => {
                if (!(!isMyTurn || step === 3 || spunModes.dare || isDone)) {
                  spinDare(luckyUser?.drinkType);
                }
              }}
              rotationDare={rotationDare}
              currentDareOptions={currentDareOptions}
            />
          </Grid>
        </Grid>

        {/* Result Display */}
        {displayResult && (
          <BottleResult
            displayResult={displayResult}
            isDone={isDone}
            lastModeResult={lastModeResult}
            luckyUser={luckyUser}
            isMyTurn={isMyTurn}
            myPrivileges={myPrivileges}
            handleSkipDare={skipDare}
            handleConfirmTask={confirmTask}
          />
        )}

        {/* Footer / Admin */}
        <BottleFooter
          newQuestion={newQuestion}
          setNewQuestion={setNewQuestion}
          myPrivileges={myPrivileges}
          handleAddQuestion={() => {
            addQuestion(newQuestion);
            setNewQuestion("");
          }}
          handleResetRoom={resetRoom}
          roomCode={roomCode}
        />
      </ContainerStyle>
    </motion.div>
  );
};
